import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Button, CircularProgress } from '@material-ui/core';
import { FormikProvider, useFormik } from 'formik';

import DynamicTable from '~/components/DynamicTable';
import File from '~/components/Formik/File';
import Input from '~/components/Formik/Input';
import InputMask from '~/components/Formik/InputMask';
import { useAuth } from '~/hooks/auth';
import { useFiles } from '~/hooks/files';
import { IState } from '~/store';
import {
  getProcessesExecutionsRequest,
  getProcessesSolicitationsRequest,
} from '~/store/modules/processes/actions';
import { IProcess, IProcessFile } from '~/store/modules/processes/types';
import { ITask } from '~/store/modules/tasks/types';
import { profiles } from '~/types/constants';

import BooleanField from '../BooleanField';
import DataListInput, { DataListValue } from '../InputSearch/DataListInput';
import SelectListInput, {
  SelectListValue,
} from '../InputSearch/SelectListInput';
import SelectModal from '../Select/SelectModal';
import {
  createCamundaForm,
  getInitialFormValues,
  hasEnumOptions,
} from './service/parser';
import { ProcessFormField, ProcessFormValues } from './service/parser/types';
import { createYupValidationSchemaFromForm } from './service/validation';
import { Validations } from './service/validation/types';
import { createCamundaVariable } from './service/variable';
import { ProcessSubmitVariables } from './service/variable/types';
import { RequiredText, StyledForm, DynamicTableContainer } from './styles';

interface FormCamundaProps {
  process: IProcess | ITask;
  onSubmit: (
    variables: ProcessSubmitVariables,
    processFiles: Array<IProcessFile>,
  ) => void;
  onDirty?: (isDirty: boolean) => void;
}

const FormCamunda: React.FC<FormCamundaProps> = ({
  process,
  children,
  onSubmit,
  onDirty,
}): JSX.Element => {
  const [processFiles, setProcessFiles] = useState<IProcessFile[]>([]);

  const form = useMemo(() => createCamundaForm(process?.form ?? []), [process]);

  const [dynamicTableValues, setDynamicTableValues] = useState([]);

  const validationSchema = useMemo(
    () => createYupValidationSchemaFromForm(form),
    [form],
  );

  const initialValues: ProcessFormValues = useMemo(
    () => getInitialFormValues(form),
    [form],
  );

  const loadingProcessInstance = useSelector(
    (state: IState) => state.processes.loadingProcessInstance,
  );
  const loadingExecution = useSelector(
    (state: IState) => state.tasks.loadingExecution,
  );

  const {
    uploadedFiles,
    resetUploadedFiles,
    resetHistoryFiles,
    numberOfFilesToUpload,
    handleGetHistory,
  } = useFiles();

  const dispatch = useDispatch();

  const { checkProfile, user } = useAuth();

  const handleSubmit = async (data: ProcessFormValues) => {
    // eslint-disable-next-line no-prototype-builtins
    if (Object(data).hasOwnProperty('dynamicTable')) {
      Object.assign(data, {
        ...data,
        dynamicTable: dynamicTableValues,
      });
    }

    const formFields = handleRenderForm();
    const variables: ProcessSubmitVariables = {};
    const filteredData: ProcessSubmitVariables = {};
    form.forEach((field) => {
      const {
        id,
        validations: { isFile },
      } = field;
      if (!isFile) {
        variables[id] = createCamundaVariable(field, data[id]);
      }
    });

    // eslint-disable-next-line no-restricted-syntax
    for (const field of formFields) {
      Object.entries(variables).forEach(([key, value]) => {
        if (key !== 'dynamicTable' && field.key === key) {
          Object.assign(filteredData, {
            ...filteredData,
            [key]: value,
          });
        } else {
          Object.assign(filteredData, {
            ...filteredData,
            [key]: value,
          });
        }
      });
    }
    const variablesWithValidValues = Object.fromEntries(
      Object.entries(filteredData).filter(([, value]) => {
        if (value && value.value !== 'Invalid date') {
          return value;
        }
        return false;
      }),
    );
    if (isNewProcessRequest(process)) {
      const processVariables = getNewProcessBaseVariables();
      Object.entries(processVariables).forEach(([key, value]) => {
        Object.assign(variablesWithValidValues, {
          ...variablesWithValidValues,
          [key]: value,
        });
      });
    }

    onSubmit(variablesWithValidValues, processFiles);
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema,
    validateOnChange: false,
  });

  const getNewProcessBaseVariables = (): ProcessSubmitVariables => {
    return {
      registradoPorId: {
        value: String(user.personId),
        type: 'String',
      },
      registradoPor: {
        value: `${user.firstName} ${user.lastName}`,
        type: 'String',
      },
      responsavelPorId: {
        value:
          checkProfile(profiles.CLIENT) || checkProfile(profiles.SUPPLIER)
            ? null
            : String(user.personId),
        type: 'String',
      },
    };
  };

  const handleSubmitTableValues = (term: any) => {
    setDynamicTableValues(term);
  };

  const handleRenderForm = () => {
    const filteredForm = filterEligibleInputs();

    return filteredForm.map((field) => convertFieldToReactField(field));
  };

  const filterEligibleInputs = () => {
    return form.filter((input) => {
      const {
        conditionalInput,
        conditionalAction,
        conditionalValue,
      } = input.validations;

      if (conditionalAction && conditionalInput) {
        const refInput = form.find((field) => field.id === conditionalInput);
        const refInputCamundaVariable = refInput
          ? createCamundaVariable(refInput, formik.values[conditionalInput])
          : null;

        return (
          (conditionalAction === 'show' &&
            refInputCamundaVariable?.value === conditionalValue) ||
          (conditionalAction === 'filter' && refInputCamundaVariable?.value)
        );
      }
      return true;
    });
  };

  const convertFieldToReactField = (field: ProcessFormField): JSX.Element => {
    const { id, type, label: rawLabel } = field;
    const {
      dataListName,
      execList,
      isDateTime,
      isFile,
      isMulti,
      isReadonly,
      isRequired,
      mask,
      slList,
    } = field.validations;
    const label = isRequired ? `${rawLabel} *` : rawLabel;

    if (id === 'dynamicTable') {
      return (
        <DynamicTableContainer id={id}>
          <DynamicTable
            propTest={handleSubmitTableValues}
            values={field.defaultValue}
            buttonName={field.validations.dynamicTableName}
          />
        </DynamicTableContainer>
      );
    }

    if (isFile) {
      return (
        <File
          key={id}
          formFieldId={id}
          label={label}
          isRequired={field.validations.isRequired}
        />
      );
    }

    if (type === 'long') {
      return (
        <Input
          type="number"
          step="1"
          min={0}
          inputMode="numeric"
          key={id}
          name={id}
          label={label}
          disabled={isReadonly}
          isRequired={field.validations.isRequired}
        />
      );
    }

    if (isDateTime) {
      return (
        <InputMask
          key={id}
          type="tel"
          label={label}
          name={id}
          placeholder="__/__/____"
          mask="99/99/9999"
          disabled={isReadonly}
          isRequired={field.validations.isRequired}
        />
      );
    }

    if (dataListName) {
      const parent = getParentValues<DataListValue>(field.validations);
      return (
        <DataListInput
          key={id}
          fieldId={id}
          fieldName={id}
          fieldLabel={label}
          dataListId={dataListName}
          isMulti={isMulti}
          isDisabled={isReadonly}
          parentData={{ value: parent, strictChildren: true }}
          isRequired={field.validations.isRequired}
        />
      );
    }

    if (slList) {
      return (
        <SelectListInput
          key={id}
          fieldId={id}
          fieldName={id}
          fieldLabel={label}
          idValue="id"
          labelValue="nomeSolicitacao"
          value="nomeSolicitacao"
          pathToList="processes"
          pathToLoading="loadingProcessesSolicitations"
          pathToTotal="processesSolicitationsTotal"
          pathToData="processesSolicitations"
          isMulti={isMulti}
          onLoad={(search, page, limit) =>
            dispatch(
              getProcessesSolicitationsRequest({
                nomeSolicitacao: search,
                page,
                limit,
              }),
            )
          }
        />
      );
    }

    if (execList) {
      const parent = getParentValues<SelectListValue>(field.validations);
      const superProcessInstanceId =
        parent instanceof Array ? parent[0].id : parent;

      return (
        <SelectListInput
          key={id}
          fieldId={id}
          fieldName={id}
          fieldLabel={label}
          isMulti={isMulti}
          idValue="id"
          labelValue="nomeExecucao"
          value="nomeExecucao"
          pathToList="processes"
          pathToLoading="loadingProcessesExecutions"
          pathToTotal="processesExecutionsTotal"
          pathToData="processesExecutions"
          onLoad={(search, page, limit) =>
            dispatch(
              getProcessesExecutionsRequest({
                ...(parent && {
                  superProcessInstanceId,
                }),
                executionName: search,
                hasExecutionName: true,
                page,
                limit,
              }),
            )
          }
          isDisabled={isReadonly}
          solicitationData={{
            id: superProcessInstanceId,
            strictChildren: true,
          }}
        />
      );
    }

    if (type === 'boolean') {
      return (
        <BooleanField
          key={id}
          name={id}
          id={id}
          label={label}
          disabled={isReadonly}
        />
      );
    }

    if (type === 'enum') {
      return (
        <SelectModal
          key={id}
          name={id}
          label={label}
          placeholder="Selecione uma opção"
          options={hasEnumOptions(field) ? field.options : []}
          isDisabled={isReadonly}
          maxMenuHeight={200}
          isClearable={!isRequired}
          isRequired={field.validations.isRequired}
        />
      );
    }

    if (mask) {
      return (
        <InputMask
          key={id}
          label={label}
          name={id}
          mask={mask}
          disabled={isReadonly}
          isRequired={field.validations.isRequired}
        />
      );
    }
    return (
      <Input
        key={id}
        name={id}
        label={label}
        disabled={isReadonly}
        isRequired={field.validations.isRequired}
        isTextOnly={id === 'quebraTexto'}
      />
    );
  };

  const getParentValues = <T,>(validations: Validations) => {
    let parent: Array<T> | string | undefined;

    const { conditionalInput, conditionalAction } = validations;

    if (conditionalInput && conditionalAction === 'filter') {
      parent = formik.values[conditionalInput] as Array<T> | string | undefined;
    }

    if (parent instanceof Array) {
      return parent.length > 0 ? parent : undefined;
    }

    return parent;
  };

  const isUserTask = (process: IProcess | ITask): process is ITask => {
    return !!(process as ITask).processInstanceId;
  };

  const isNewProcessRequest = (
    process: IProcess | ITask,
  ): process is IProcess => {
    return !isUserTask(process);
  };

  useEffect(() => {
    resetHistoryFiles();
    resetUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUserTask(process)) {
      handleGetHistory(process.processInstanceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process]);

  useEffect(() => {
    const newProcessFiles: IProcessFile[] = uploadedFiles.map((file) => {
      const task = process as ITask;
      const taskProps = {
        process_instance_id: task.processInstanceId,
        super_process_instance_id: task.superProcessInstanceId,
        root_process_instance_id: task.rootProcessInstanceId,
        task_id: task.id,
      };

      return {
        process_instance_id: null,
        super_process_instance_id: null,
        root_process_instance_id: null,
        task_id: null,
        path: encodeURIComponent(file.url),
        form_field_id: file.formFieldId,
        file_name: file.name,
        s3_file_name: file.key,
        ...(isUserTask(process) && taskProps),
      };
    });

    setProcessFiles(newProcessFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles]);

  useEffect(() => {
    if (onDirty) {
      onDirty(formik.dirty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.dirty]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      toast.error('Erro na validação do formulário');
    }
  }, [formik.errors]);

  return (
    <FormikProvider value={formik}>
      <StyledForm onSubmit={formik.handleSubmit}>
        {handleRenderForm()}
        {isUserTask(process) && (
          <RequiredText>*Campos Obrigatórios</RequiredText>
        )}

        <Button
          type="submit"
          disabled={numberOfFilesToUpload > 0}
          color="primary"
          variant="contained"
          fullWidth
          style={isNewProcessRequest(process) ? { marginTop: '80px' } : {}}
        >
          {loadingProcessInstance || loadingExecution ? (
            <CircularProgress size={24} color="inherit" />
          ) : process && process.startableInTasklist ? (
            'Registrar solicitação'
          ) : (
            'Executar tarefa'
          )}
        </Button>
      </StyledForm>
      {children}
    </FormikProvider>
  );
};

export default FormCamunda;
